const REQUIRED_COLUMNS = ['time', 'open', 'high', 'low', 'close'];

const roundMetric = (value) => Number(value.toFixed(6));

const formatDate = (unixSeconds) => {
  const date = new Date(unixSeconds * 1000);
  return date.toISOString().slice(0, 10);
};

const median = (values) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle];
  return (sorted[middle - 1] + sorted[middle]) / 2;
};

export function parseCsvCandles(csvText) {
  const lines = csvText.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) {
    throw new Error('CSV 数据不足，至少需要表头和一行 K 线数据');
  }

  const headers = lines[0].split(',').map((header) => header.trim().toLowerCase());
  const columnIndex = Object.fromEntries(headers.map((header, index) => [header, index]));

  REQUIRED_COLUMNS.forEach((column) => {
    if (columnIndex[column] === undefined) {
      throw new Error(`CSV 缺少必要字段: ${column}`);
    }
  });

  return lines.slice(1).map((line, rowIndex) => {
    const values = line.split(',').map((value) => value.trim().replace(/^"|"$/g, ''));
    const candle = {
      time: Number(values[columnIndex.time]),
      open: Number(values[columnIndex.open]),
      high: Number(values[columnIndex.high]),
      low: Number(values[columnIndex.low]),
      close: Number(values[columnIndex.close]),
    };

    if (Object.values(candle).some((value) => !Number.isFinite(value))) {
      throw new Error(`CSV 第 ${rowIndex + 2} 行包含无法解析的数值`);
    }

    return {
      time: candle.time,
      date: formatDate(candle.time),
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    };
  }).sort((a, b) => a.time - b.time);
}

export function normalizeCloses(candles) {
  if (!candles.length || candles[0].close === 0) {
    throw new Error('无法标准化空数据或首个收盘价为 0 的数据');
  }

  const base = candles[0].close;
  return candles.map((candle) => roundMetric(candle.close / base - 1));
}

export function euclideanDistance(a, b) {
  if (a.length !== b.length) {
    throw new Error('欧氏距离要求两个序列长度一致');
  }

  const sumSquares = a.reduce((sum, value, index) => {
    const diff = value - b[index];
    return sum + diff * diff;
  }, 0);

  return Math.sqrt(sumSquares);
}

export function calculateForwardMetrics(candles, segmentEndIndex, forwardWindow) {
  const baseClose = candles[segmentEndIndex]?.close;
  const future = candles.slice(segmentEndIndex + 1, segmentEndIndex + 1 + forwardWindow);

  if (!baseClose || future.length < forwardWindow) {
    throw new Error('后续观察窗口数据不足');
  }

  const finalClose = future[future.length - 1].close;
  const lowestLow = Math.min(...future.map((candle) => candle.low));
  const highestHigh = Math.max(...future.map((candle) => candle.high));

  return {
    forwardReturn: roundMetric(finalClose / baseClose - 1),
    maxDrawdown: roundMetric(lowestLow / baseClose - 1),
    maxRunup: roundMetric(highestHigh / baseClose - 1),
  };
}

export function findPatternMatches(candles, options = {}) {
  const patternLength = Number(options.patternLength ?? 20);
  const matchCount = Number(options.matchCount ?? 50);
  const forwardWindow = Number(options.forwardWindow ?? 10);
  const exclusionWindow = Number(options.exclusionWindow ?? 30);

  if ([patternLength, matchCount, forwardWindow, exclusionWindow].some((value) => !Number.isFinite(value) || value < 1)) {
    throw new Error('参数必须是大于 0 的数字');
  }

  const requiredRows = patternLength + forwardWindow + exclusionWindow + 1;
  if (candles.length < requiredRows) {
    throw new Error(`K 线数据不足，至少需要 ${requiredRows} 行`);
  }

  const currentStartIndex = candles.length - patternLength;
  const currentSegment = candles.slice(currentStartIndex);
  const currentPattern = normalizeCloses(currentSegment);
  const lastCandidateStart = candles.length - patternLength - forwardWindow - exclusionWindow;
  const candidates = [];

  for (let startIndex = 0; startIndex <= lastCandidateStart; startIndex += 1) {
    const endIndex = startIndex + patternLength - 1;
    const candidateSegment = candles.slice(startIndex, startIndex + patternLength);
    const candidatePattern = normalizeCloses(candidateSegment);
    const distance = euclideanDistance(currentPattern, candidatePattern);
    const metrics = calculateForwardMetrics(candles, endIndex, forwardWindow);
    const observationEndIndex = endIndex + forwardWindow;

    candidates.push({
      rank: 0,
      startIndex,
      endIndex,
      observationEndIndex,
      startDate: candles[startIndex].date,
      endDate: candles[endIndex].date,
      observationEndDate: candles[observationEndIndex].date,
      distance: roundMetric(distance),
      pattern: candidatePattern,
      candles: candidateSegment,
      forwardCandles: candles.slice(endIndex + 1, observationEndIndex + 1),
      ...metrics,
    });
  }

  return candidates
    .sort((a, b) => a.distance - b.distance)
    .slice(0, matchCount)
    .map((match, index) => ({ ...match, rank: index + 1 }));
}

export function summarizeMatches(matches) {
  if (!matches.length) {
    return {
      upProbability: 0,
      averageReturn: 0,
      medianReturn: 0,
      worstReturn: 0,
      averageMaxDrawdown: 0,
      bestRunup: 0,
    };
  }

  const returns = matches.map((match) => match.forwardReturn);
  const drawdowns = matches.map((match) => match.maxDrawdown);
  const runups = matches.map((match) => match.maxRunup);
  const average = (values) => values.reduce((sum, value) => sum + value, 0) / values.length;

  return {
    upProbability: roundMetric(returns.filter((value) => value > 0).length / returns.length),
    averageReturn: roundMetric(average(returns)),
    medianReturn: roundMetric(median(returns)),
    worstReturn: roundMetric(Math.min(...returns)),
    averageMaxDrawdown: roundMetric(average(drawdowns)),
    bestRunup: roundMetric(Math.max(...runups)),
  };
}
